"use client"

import { useState } from "react"
import { useRouter } from "next/navigation"
import { Header } from "@/components/landrisk/header"
import { Footer } from "@/components/landrisk/footer"
import { StepIndicator } from "@/components/landrisk/step-indicator"
import { GoogleMapWrapper } from "@/components/landrisk/google-map-wrapper"
import { ProfileModal } from "@/components/landrisk/profile-modal"
import { useLandRisk } from "@/lib/landrisk-context"
import type { SelectedLocation } from "@/lib/types"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { MapPin, Check, ArrowRight, Share2, Lock, BarChart3 } from "lucide-react"

export default function IdentifyPage() {
  const router = useRouter()
  const { location, setLocation } = useLandRisk()
  const [tempLocation, setTempLocation] = useState<SelectedLocation | null>(
    location
  )
  const [profileModalOpen, setProfileModalOpen] = useState(false)

  const handleLocationSelect = (loc: SelectedLocation) => {
    setTempLocation(loc)
  }

  const handleContinue = () => {
    if (tempLocation) {
      setLocation(tempLocation)
      router.push("/pricing")
    }
  }

  const handleShare = async () => {
    const text = `Discover LANDRISK - Get comprehensive land risk analysis including flood risks, legal issues, and valuations for informed land buying decisions. 🏞️`
    const url = "https://landrisk.app"
    
    if (navigator.share) {
      try {
        await navigator.share({ title: "LANDRISK", text, url })
      } catch (err) {
        // User cancelled share
      }
    } else {
      // Fallback: copy to clipboard
      await navigator.clipboard.writeText(`${text}\n${url}`)
    }
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header onProfileClick={() => setProfileModalOpen(true)} />
      <main className="flex-1 bg-muted/20">
        <div className="mx-auto max-w-5xl px-4 py-8 lg:px-8">
          <div className="mb-8">
            <StepIndicator currentStep={1} />
          </div>

          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              Identify Your Parcel
            </h1>
            <p className="mt-2 text-muted-foreground">
              Search for an area, drop a pin on the map, or enter GPS coordinates
            </p>
          </div>

          <GoogleMapWrapper onLocationSelect={handleLocationSelect} />

          {tempLocation && (
            <Card className="mt-6">
              <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <MapPin className="size-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-foreground">
                        {tempLocation.areaName}
                      </p>
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                        <Check className="size-3" />
                        Located
                      </span>
                    </div>
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      {tempLocation.county} County &middot;{" "}
                      {tempLocation.lat.toFixed(4)}, {tempLocation.lng.toFixed(4)}
                    </p>
                  </div>
                </div>
                <Button
                  size="lg"
                  onClick={handleContinue}
                  className="gap-2"
                >
                  Continue to Pricing
                  <ArrowRight className="size-4" />
                </Button>
              </CardContent>
            </Card>
          )}

          {!tempLocation && (
            <Card className="mt-6 border-dashed">
              <CardContent className="flex items-center justify-center py-6">
                <p className="text-sm text-muted-foreground">
                  Select a location on the map to continue
                </p>
              </CardContent>
            </Card>
          )}

          <div className="mt-8 grid gap-4 md:grid-cols-2">
            <Card className="bg-muted/30">
              <CardContent className="flex flex-col items-center gap-4 py-6 text-center">
                <div>
                  <h3 className="font-semibold text-foreground">Save Your Reports</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Create a profile to track multiple properties and access your historical reports anytime
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setProfileModalOpen(true)}
                  className="gap-2"
                >
                  <Lock className="size-4" />
                  Create Profile
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-muted/30">
              <CardContent className="flex flex-col items-center gap-4 py-6 text-center">
                <div>
                  <h3 className="font-semibold text-foreground">Share with Friends</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Help your network make better land buying decisions with LANDRISK
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleShare}
                  className="gap-2"
                >
                  <Share2 className="size-4" />
                  Share App
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      <Footer />

      <ProfileModal open={profileModalOpen} onOpenChange={setProfileModalOpen} />
    </div>
  )
}
